import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { Alert, Button } from '../../components/ui'
import type { Unit } from '../../api/objects'
import { deleteProperty, deleteUnit } from '../../api/objects-write'
import { t } from '../../i18n'
import { unitKindLabel } from '../../labels'

/**
 * Removing a space, or a whole address.
 *
 * Not a `window.confirm`. What goes with a flat is its rooms, the places in
 * them, every price on any of it and every photograph -- and a browser dialog
 * can only ask "are you sure?" about a thing it cannot describe. Somebody who
 * has just spent ten minutes entering three rooms deserves to be told that all
 * three are about to go, in the same breath as being asked.
 *
 * The one piece of reassurance is worth as much as the warning: what a client
 * was already shown survives. Snapshots outlive the offer they were taken from
 * (migration 0012), so deleting a flat empties the inventory and not the record
 * of what people were told about it. Without knowing that, the honest thing for
 * a cautious person to do is never delete anything, and an inventory nobody
 * prunes stops being an inventory.
 */
export function DeleteSpace({
  unit,
  units,
  propertyID,
}: {
  readonly unit: Unit
  /** Every space at this address, so the warning can count what goes with it. */
  readonly units: readonly Unit[]
  readonly propertyID: string
}) {
  const [asking, setAsking] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const client = useQueryClient()

  const remove = useMutation({
    mutationFn: () => deleteUnit(unit.id),
    onSuccess: () => {
      setAsking(false)
      setFailure(null)
      void client.invalidateQueries({ queryKey: ['property', propertyID] })
    },
    onError: (error: Error) => setFailure(error.message),
  })

  if (!asking) {
    return (
      <Button variant="secondary" onClick={() => setAsking(true)}>
        {t('build.remove')}
      </Button>
    )
  }

  const inside = descendantsOf(unit.id, units)

  return (
    <div className="build-form build-form--danger">
      <p className="build-form__hint">
        {t('build.removeAsk', { what: nameOf(unit) })}
      </p>

      {inside.length === 0 ? null : (
        <p className="build-form__loss">{t('build.removeTakes', { list: summarise(inside) })}</p>
      )}

      <p className="build-form__hint">{t('build.removeKeeps')}</p>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
          {t('build.removeConfirm')}
        </Button>
        <Button variant="secondary" onClick={() => setAsking(false)} disabled={remove.isPending}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}

/** Removing the address, and therefore everything at it. */
export function DeleteProperty({
  propertyID,
  address,
  units,
}: {
  readonly propertyID: string
  readonly address: string
  readonly units: readonly Unit[]
}) {
  const [asking, setAsking] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const client = useQueryClient()
  const navigate = useNavigate()

  const remove = useMutation({
    mutationFn: () => deleteProperty(propertyID),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['properties'] })
      // Back to the list: staying on the card of a thing that no longer exists
      // would answer the next refresh with a "not found" nobody asked for.
      void navigate('/objects')
    },
    onError: (error: Error) => setFailure(error.message),
  })

  if (!asking) {
    return (
      <Button variant="secondary" onClick={() => setAsking(true)}>
        {t('build.removeAddress')}
      </Button>
    )
  }

  return (
    <div className="build-form build-form--danger">
      <p className="build-form__hint">{t('build.removeAddressAsk', { address })}</p>

      {units.length === 0 ? null : (
        <p className="build-form__loss">{t('build.removeTakes', { list: summarise(units) })}</p>
      )}

      <p className="build-form__hint">{t('build.removeKeeps')}</p>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
          {t('build.removeConfirm')}
        </Button>
        <Button variant="secondary" onClick={() => setAsking(false)} disabled={remove.isPending}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}

/** Everything inside this space, however deep. */
function descendantsOf(id: string, units: readonly Unit[]): readonly Unit[] {
  const found: Unit[] = []
  const pending = [id]

  while (pending.length > 0) {
    const parent = pending.pop()
    for (const unit of units) {
      if (unit.parentUnitId !== parent) continue
      found.push(unit)
      pending.push(unit.id)
    }
  }
  return found
}

/**
 * "2 комнаты, 1 место, 4 файла" — counted rather than listed, because a flat
 * with nine rooms would otherwise produce a warning nobody finishes reading.
 */
function summarise(units: readonly Unit[]): string {
  const parts: string[] = []

  for (const kind of ['apartment', 'room', 'bed'] as const) {
    const count = units.filter((unit) => unit.kind === kind).length
    if (count > 0) parts.push(t(`build.count.${kind}`, { count }))
  }

  const files = units.reduce(
    (total, unit) => total + unit.listings.reduce((sum, offer) => sum + offer.media.length, 0),
    0,
  )
  if (files > 0) parts.push(t('build.count.files', { count: files }))

  return parts.join(', ')
}

function nameOf(unit: Unit): string {
  return unit.label === '' ? unitKindLabel(unit.kind).toLowerCase() : `«${unit.label}»`
}
